import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Sparkles, ChevronDown } from 'lucide-react';

export default function LoginPage() {
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [agreed, setAgreed] = useState(false);
  // How-it-Works: expanded by default on desktop (≥640px), collapsed on mobile.
  const [showHIW, setShowHIW] = useState(() => {
    try { return typeof window !== 'undefined' && window.matchMedia('(min-width: 640px)').matches; }
    catch { return true; }
  });
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isRegister && !agreed) {
      toast.error('Please agree to the Terms and Privacy Policy to continue');
      return;
    }
    if (isRegister && password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setSubmitting(true);
    try {
      if (isRegister) {
        const res = await api.post('/auth/register', { email, password, name });
        login(res.data);
        toast.success('Welcome to your journey');
      } else {
        const res = await api.post('/auth/login', { email, password });
        login(res.data);
        toast.success('Welcome back');
      }
      navigate('/', { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? (detail[0]?.msg || 'Please check your details')
        : (typeof detail === 'string' ? detail : 'Something went wrong');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-sand relative overflow-hidden" data-testid="login-page">
      <div className="noise-overlay" />
      <div className="ethereal-glow-gold animate-drift" style={{ top: '-150px', right: '-100px' }} />
      <div className="ethereal-glow-moss animate-drift-slow" style={{ bottom: '-150px', left: '-100px' }} />
      <div className="ethereal-glow-warm" style={{ top: '40%', left: '30%' }} />
      {/* Floating orbs */}
      <div className="floating-orb floating-orb-gold" style={{ width: 60, height: 60, top: '20%', right: '25%', animationDuration: '8s', zIndex: 1 }} />
      <div className="floating-orb floating-orb-moss" style={{ width: 40, height: 40, bottom: '30%', left: '10%', animationDuration: '10s', animationDelay: '3s', zIndex: 1 }} />

      <div className="relative z-10 flex min-h-screen">
        {/* Left: Hero Image */}
        <div className="hidden lg:flex lg:w-1/2 relative items-center justify-center p-12">
          <div className="relative w-full max-w-lg animate-fade-up">
            <img
              src="https://images.pexels.com/photos/668353/pexels-photo-668353.jpeg"
              alt="Balance stones"
              className="rounded-3xl object-cover w-full h-[600px] shadow-lg"
            />
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-t from-dark/40 to-transparent" />
            <div className="absolute bottom-8 left-8 right-8 text-bone">
              <h2 className="font-serif text-4xl font-light leading-tight mb-3">
                You manifest who you are,<br />not what you want.
              </h2>
              <p className="font-sans text-sm opacity-80 tracking-wide">
                Embody the identity of your highest self
              </p>
            </div>
          </div>
        </div>

        {/* Right: Auth Form */}
        <div className="flex-1 flex items-center justify-center p-8 lg:p-16">
          <div className="w-full max-w-md animate-fade-up delay-200">
            <div className="mb-12">
              <img
                src="/wordmark.png"
                alt="Live in Vibrancy"
                data-testid="login-wordmark"
                className="w-56 h-auto mb-4 select-none"
                draggable={false}
              />
              <p className="text-gold font-sans text-xs tracking-[0.3em] uppercase mb-3">
                Inner Work to reveal the powerful Identity already within you ✨
              </p>
              <h1 className="font-serif text-4xl sm:text-5xl font-light text-dark tracking-tight leading-tight mystical-heading shimmer-text">
                {isRegister ? 'Begin Your Journey' : 'Welcome Back'}
              </h1>
              <p className="text-text-muted font-sans text-base mt-4 leading-relaxed">
                {isRegister
                  ? 'Step into the version of you that already has everything you desire.'
                  : 'Continue embodying your highest self.'}
              </p>

              {/* How it Works — shown on Create Account view only.
                  Collapsed by default on mobile to keep the form above the fold. */}
              {isRegister && (
                <div className="mt-6" data-testid="how-it-works">
                  <button
                    type="button"
                    onClick={() => setShowHIW(v => !v)}
                    data-testid="how-it-works-toggle"
                    aria-expanded={showHIW}
                    className="flex items-center gap-2 group focus:outline-none"
                  >
                    <span className="text-gold font-sans text-[10px] tracking-[0.35em] uppercase">
                      How It Works
                    </span>
                    <ChevronDown
                      size={14}
                      className="text-gold transition-transform"
                      style={{ transform: showHIW ? 'rotate(180deg)' : 'rotate(0deg)' }}
                    />
                  </button>

                  <div
                    className="grid transition-[grid-template-rows] duration-500 ease-in-out"
                    style={{ gridTemplateRows: showHIW ? '1fr' : '0fr' }}
                    data-testid="how-it-works-collapsible"
                  >
                    <ul
                      className="overflow-hidden space-y-3 font-sans text-sm text-text-muted leading-relaxed"
                      style={{ marginTop: showHIW ? '0.75rem' : '0', transition: 'margin-top 0.4s ease' }}
                    >
                      <li className="flex gap-3" data-testid="how-it-works-bullet-1">
                        <Sparkles size={14} className="text-gold shrink-0 mt-1" />
                        <span>
                          <span className="text-dark font-medium">Become the version of you that already has it</span>
                          {' '}— by catching the old story through journaling and gentle pattern recognition.
                          Identity work, not affirmations.
                        </span>
                      </li>
                      <li className="flex gap-3" data-testid="how-it-works-bullet-2">
                        <Sparkles size={14} className="text-gold shrink-0 mt-1" />
                        <span>
                          <span className="text-dark font-medium">A safe space for the messy in-between</span>
                          {' '}— practice the new you with breathwork, meditations, and an AI coach
                          that's here when you're stuck.
                        </span>
                      </li>
                      <li className="flex gap-3" data-testid="how-it-works-bullet-3">
                        <Sparkles size={14} className="text-gold shrink-0 mt-1" />
                        <span>
                          <span className="text-dark font-medium">Notice the universe meeting you halfway</span>
                          {' '}— track the small evidence that proves the work is working, and watch it multiply.
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              )}

              <div className="mystical-divider mt-6" />
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {isRegister && (
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-text-muted text-xs tracking-wider uppercase font-sans">Your Name</Label>
                  <Input
                    id="name"
                    data-testid="register-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your full name"
                    required
                    className="h-12 bg-bone/60 border-black/8 focus:border-gold focus:ring-gold/30 rounded-xl font-sans"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-text-muted text-xs tracking-wider uppercase font-sans">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="h-12 bg-bone/60 border-black/8 focus:border-gold focus:ring-gold/30 rounded-xl font-sans"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-text-muted text-xs tracking-wider uppercase font-sans">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    data-testid="login-password-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Your password"
                    required
                    className="h-12 bg-bone/60 border-black/8 focus:border-gold focus:ring-gold/30 rounded-xl font-sans pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-dark"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {isRegister && (
                <label className="flex items-start gap-3 cursor-pointer group" data-testid="terms-checkbox-wrapper">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    data-testid="terms-checkbox"
                    className="mt-1 w-4 h-4 accent-[#C6B28F] cursor-pointer"
                  />
                  <span className="font-sans text-xs text-text-muted leading-relaxed">
                    I agree to the{' '}
                    <Link
                      to="/terms"
                      target="_blank"
                      data-testid="terms-link"
                      className="text-dark underline decoration-gold/40 hover:decoration-gold"
                    >
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link
                      to="/privacy"
                      target="_blank"
                      data-testid="privacy-link"
                      className="text-dark underline decoration-gold/40 hover:decoration-gold"
                    >
                      Privacy Policy
                    </Link>
                    .
                  </span>
                </label>
              )}

              <Button
                type="submit"
                data-testid="login-submit-button"
                disabled={submitting || (isRegister && !agreed)}
                className="w-full h-12 bg-dark hover:bg-dark/90 text-bone rounded-xl font-sans text-sm tracking-wider uppercase btn-hover disabled:opacity-60"
              >
                {submitting ? 'Please wait...' : isRegister ? 'Create Account' : 'Sign In'}
              </Button>

              {!isRegister && (
                <div className="text-center">
                  <Link
                    to="/forgot-password"
                    data-testid="forgot-password-link"
                    className="text-text-muted hover:text-dark font-sans text-xs tracking-wider underline underline-offset-4 decoration-gold/30 hover:decoration-gold"
                  >
                    Forgot your password?
                  </Link>
                </div>
              )}
            </form>

            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-black/8" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-sand px-4 text-xs text-text-muted uppercase tracking-widest font-sans">or</span>
              </div>
            </div>

            <Button
              variant="outline"
              data-testid="google-login-button"
              onClick={handleGoogleLogin}
              className="w-full h-12 border-black/10 bg-bone/40 hover:bg-bone/80 rounded-xl font-sans text-sm tracking-wider btn-hover"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </Button>

            <p className="text-center mt-8 text-text-muted text-sm font-sans">
              {isRegister ? 'Already on this path?' : "Ready to begin?"}{' '}
              <button
                onClick={() => setIsRegister(!isRegister)}
                data-testid="toggle-auth-mode"
                className="text-dark font-medium underline underline-offset-4 decoration-gold/40 hover:decoration-gold"
              >
                {isRegister ? 'Sign In' : 'Create Account'}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
